import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { PublicNavbar } from '../components/PublicNavbar'
import { PublicFooter } from '../components/PublicFooter'
import { supabase } from '../lib/supabase'

/**
 * Request a password reset email. Reachable directly (no email required up front); if the
 * user came from the login page with an email typed in, it's prefilled but not required.
 */
export const ForgotPasswordPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const prefill = (location.state as { email?: string } | null)?.email ?? ''
  const [email, setEmail] = useState(prefill)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setMessage(null)
    setIsSubmitting(true)
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      })
      if (resetError) throw resetError
      // Generic message so we don't reveal whether an account exists for that email.
      setMessage('If an account exists for that email, a password reset link is on its way.')
    } catch (err: any) {
      setError(err?.message ?? 'Failed to send reset email. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PublicNavbar />
      <Container
        maxWidth="sm"
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 520 }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <Typography variant="h4">Reset your password</Typography>
                <Typography color="text.secondary">
                  Enter your email and we'll send you a link to set a new password.
                </Typography>
                {error && <Alert severity="error">{error}</Alert>}
                {message && <Alert severity="success">{message}</Alert>}
                <TextField
                  label="Email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
                <Button
                  variant="contained"
                  color="warning"
                  type="submit"
                  disabled={isSubmitting || !email.trim()}
                >
                  {isSubmitting ? 'Sending...' : 'Send reset link'}
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Remembered it?{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => navigate('/login')}
                    sx={{ verticalAlign: 'baseline' }}
                  >
                    Back to login
                  </Link>
                </Typography>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Container>
      <PublicFooter />
    </Box>
  )
}
